import { useFocusEffect } from "expo-router";
import { useCallback, useMemo, useState } from "react";
import { ActivityIndicator, Platform, Pressable, ScrollView, Text, View } from "react-native";

import { COLORS, FONTS } from "../../../lib/brand";
import { PANE_THEMES } from "../../../components/brand/paneThemes";

const STUDENT_THEME = PANE_THEMES.Student;

import { StudentPortalShell } from "../../../components/student/StudentPortalShell";
import { useStudentPortalShellData } from "../../../lib/useStudentPortalShellData";
import {
  listMyCareerInterests,
  withdrawCareerInterest,
  type StudentCareerInterest,
} from "../../../lib/positions";


const STATUS_LABEL: Record<string, string> = {
  draft: "Draft",
  active: "Active",
  filled: "Filled",
  closed: "Closed",
};

export default function StudentCareerInterestsScreen() {
  const shell = useStudentPortalShellData();
  const [loading, setLoading] = useState(true);
  const [interests, setInterests] = useState<StudentCareerInterest[]>([]);

  const reload = useCallback(async () => {
    setLoading(true);
    const rows = await listMyCareerInterests();
    setInterests(rows);
    setLoading(false);
  }, []);

  useFocusEffect(
    useCallback(() => {
      void reload();
    }, [reload])
  );

  // Group by competition for the "{Competition} — {Company}" sections.
  const grouped = useMemo(() => {
    const map = new Map<string, { competition: StudentCareerInterest["competition"]; rows: StudentCareerInterest[] }>();
    for (const i of interests) {
      const key = i.competition.id;
      const existing = map.get(key);
      if (existing) existing.rows.push(i);
      else map.set(key, { competition: i.competition, rows: [i] });
    }
    return Array.from(map.values());
  }, [interests]);

  return (
    <StudentPortalShell
      topbarTitle="Career interests"
      topbarFirstName={shell.topbarFirstName}
      avatarName={shell.avatarName}
      avatarUrl={shell.avatarUrl}
      identity={shell.identity}
      casesBadgeCount={shell.casesBadgeCount}
      hideSidebar
    >
      <ScrollView style={{ flex: 1, backgroundColor: COLORS.OFFW.hex }} contentContainerStyle={{ paddingBottom: 64 }}>
        <View style={{ paddingHorizontal: 22, paddingTop: 28, maxWidth: 760 }}>
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 9.5,
              letterSpacing: 1.8,
              textTransform: "uppercase",
              color: COLORS.AMBER.hex,
            }}
          >
            Career interests
          </Text>
          <Text
            style={{
              marginTop: 6,
              fontFamily: FONTS.display,
              fontSize: 36,
              lineHeight: 40,
              color: COLORS.FOREST.hex,
            }}
          >
            Roles you've opted in for
          </Text>
          <Text
            style={{
              marginTop: 8,
              fontFamily: FONTS.body,
              fontSize: 13.5,
              lineHeight: 21,
              color: STUDENT_THEME.textOnSurfaceDim,
              maxWidth: 580,
            }}
          >
            Roles appear here when you register for competitions that link to open positions.
            Companies cannot see who opted in until after judging closes.
          </Text>

          {loading ? (
            <View style={{ marginTop: 28, alignItems: "center" }}>
              <ActivityIndicator color={COLORS.AMBER.hex} />
            </View>
          ) : interests.length === 0 ? (
            <View
              style={{
                marginTop: 28,
                paddingVertical: 32,
                paddingHorizontal: 22,
                borderRadius: 8,
                borderWidth: 1,
                borderColor: STUDENT_THEME.border,
                backgroundColor: STUDENT_THEME.surfaceRaisedBg,
                alignItems: "center",
              }}
            >
              <Text style={{ fontFamily: FONTS.displayMedium, fontSize: 18, color: COLORS.FOREST.hex }}>
                You haven't opted in for any roles yet.
              </Text>
              <Text
                style={{
                  marginTop: 6,
                  fontFamily: FONTS.body,
                  fontSize: 13,
                  color: STUDENT_THEME.textOnSurfaceDim,
                  textAlign: "center",
                  maxWidth: 460,
                }}
              >
                Roles appear here when you register for competitions that link to open positions.
              </Text>
            </View>
          ) : (
            <View style={{ marginTop: 28, gap: 22 }}>
              {grouped.map((g) => (
                <View key={g.competition.id}>
                  <Text
                    style={{
                      fontFamily: FONTS.bodyBold,
                      fontSize: 9.5,
                      letterSpacing: 1.6,
                      textTransform: "uppercase",
                      color: STUDENT_THEME.textOnSurfaceFaint,
                    }}
                  >
                    {g.competition.company_name ?? "—"}
                  </Text>
                  <Text
                    style={{
                      marginTop: 4,
                      fontFamily: FONTS.displayMedium,
                      fontSize: 22,
                      color: COLORS.FOREST.hex,
                    }}
                  >
                    {g.competition.name}
                  </Text>
                  <View style={{ marginTop: 12, gap: 10 }}>
                    {g.rows.map((r) => (
                      <CareerRow
                        key={r.position.id + r.competition.id}
                        row={r}
                        onWithdrawn={() => void reload()}
                      />
                    ))}
                  </View>
                </View>
              ))}
            </View>
          )}
        </View>
      </ScrollView>
    </StudentPortalShell>
  );
}

function CareerRow({
  row,
  onWithdrawn,
}: {
  row: StudentCareerInterest;
  onWithdrawn: () => void;
}) {
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function withdraw() {
    setBusy(true);
    setErr(null);
    const { error } = await withdrawCareerInterest(row.position.id, row.competition.id);
    setBusy(false);
    if (error) {
      setErr(error.message);
      return;
    }
    onWithdrawn();
  }

  const statusLabel = STATUS_LABEL[row.position.status] ?? row.position.status;
  const statusTint =
    row.position.status === "active"
      ? COLORS.SAGE.hex
      : row.position.status === "filled"
        ? COLORS.AMBER.hex
        : STUDENT_THEME.textOnSurfaceFaint;

  return (
    <View
      style={{
        padding: 16,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: STUDENT_THEME.border,
        backgroundColor: STUDENT_THEME.surfaceRaisedBg,
      }}
    >
      <View style={{ flexDirection: "row", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
        <Text style={{ flex: 1, fontFamily: FONTS.bodyMedium, fontSize: 14.5, color: COLORS.FOREST.hex }}>
          {row.position.title}
        </Text>
        <View
          style={{
            paddingHorizontal: 8,
            paddingVertical: 2,
            borderRadius: 999,
            borderWidth: 1,
            borderColor: statusTint,
          }}
        >
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 9,
              letterSpacing: 1.2,
              textTransform: "uppercase",
              color: statusTint,
            }}
          >
            {statusLabel}
          </Text>
        </View>
      </View>
      <Text style={{ marginTop: 4, fontFamily: FONTS.body, fontSize: 12, color: STUDENT_THEME.textOnSurfaceDim }}>
        {[row.position.location, row.position.comp_band].filter((v) => !!v).join(" · ") || "—"}
      </Text>

      <View style={{ marginTop: 12, flexDirection: "row", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <Text
          style={{
            fontFamily: FONTS.body,
            fontSize: 11,
            color: STUDENT_THEME.textOnSurfaceFaint,
            flex: 1,
          }}
        >
          Opted in {new Date(row.opted_in_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
        </Text>
        {err ? (
          <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11, color: "#C0392B" }}>{err}</Text>
        ) : null}
        <Pressable
          onPress={() => {
            if (busy) return;
            if (armed) {
              setArmed(false);
              void withdraw();
            } else {
              setArmed(true);
              setTimeout(() => setArmed(false), 4000);
            }
          }}
          style={[
            {
              paddingHorizontal: 12,
              paddingVertical: 7,
              borderRadius: 4,
              borderWidth: 1,
              borderColor: armed ? "#C0392B" : "rgba(28,43,26,0.18)",
              backgroundColor: armed ? "rgba(192,57,43,0.06)" : "transparent",
            },
            Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
          ]}
        >
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 10.5,
              letterSpacing: 1.4,
              textTransform: "uppercase",
              color: armed ? "#C0392B" : STUDENT_THEME.textOnSurfaceDim,
            }}
          >
            {armed ? "Click again to confirm" : "Withdraw interest"}
          </Text>
        </Pressable>
      </View>
    </View>
  );
}
